import React, { useEffect, useRef, useState } from 'react';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import dynamic from 'next/dynamic';

import type { ImagesLocation } from 'src/components/organisms/ProductImagesCarousel/types';
import {
  trackProductVideoComplete,
  trackProductVideoExpand,
  trackProductVideoImpression,
  trackProductVideoPause,
  trackProductVideoPlay,
} from 'src/tracking/trackProductVideoEvents';
import * as styles from './ProductVideoSlide.styles';

const MuxPlayer = dynamic(() => import('@mux/mux-player-react'), { ssr: false });

export interface ProductVideoSlideProps {
  /** 1-based carousel position of the video; sent with every analytics event. */
  contentPosition: number;
  /**
   * True when this is the carousel's current slide. The player is mounted only
   * while the slide is active; leaving and returning remounts it, so playback
   * always restarts from the beginning. While inactive, only the poster shows —
   * which keeps the player (and its video bytes) off the initial page load.
   */
  isActive: boolean;
  /**
   * Carousel context for analytics: `'Main image'` for the main carousel,
   * `'Expanded image'` for the full-screen modal. Tags every event, and gates
   * the impression so it fires only in the main carousel (never full-screen).
   */
  location: ImagesLocation;
  /** When provided, an expand control opens the full-screen view. Omitted in the modal. */
  onExpand?: () => void;
  playbackId: string;
  posterUrl: string;
  title: string;
  /** Total media count in the carousel; sent with the impression event. */
  totalOptions?: number;
}

// The brand purple used across the app (theme `content.brand`).
const PLAYER_ACCENT_COLOR = `var(${themeVariables.colors.content.brand})`;

export const ProductVideoSlide = ({
  contentPosition,
  isActive,
  location,
  onExpand,
  playbackId,
  posterUrl,
  title,
  totalOptions,
}: ProductVideoSlideProps) => {
  const [paused, setPaused] = useState(false);
  const [ready, setReady] = useState(false);
  // Guards the once-per-impression complete event; reset when the slide reactivates.
  const hasCompletedRef = useRef(false);

  // Reset playback state whenever the slide (de)activates: the player remounts,
  // so it should start unpaused and fade in fresh once it begins playing. Fire
  // the impression when the video becomes the active slide — but only in the main
  // carousel, so a single product view isn't counted again from the full-screen modal.
  useEffect(() => {
    setPaused(false);
    setReady(false);
    hasCompletedRef.current = false;

    if (isActive && location === 'Main image') {
      trackProductVideoImpression({
        contentPosition,
        location,
        playbackId,
        totalOptions: totalOptions ?? 0,
      });
    }
  }, [contentPosition, isActive, location, playbackId, totalOptions]);

  // Manual toggle only — autoplay is represented by the impression, never a play event.
  const togglePlay = () => {
    const willPause = !paused;
    setPaused(willPause);

    if (willPause) {
      trackProductVideoPause({ contentPosition, location, playbackId });
    } else {
      trackProductVideoPlay({ contentPosition, location, playbackId });
    }
  };

  // Looping is done by hand (rather than the native `loop` attribute) so the
  // `ended` event fires: on the first end we record the completion, then restart
  // playback to keep the silent preview looping.
  const handleVideoEnded = (event: Event) => {
    if (!hasCompletedRef.current) {
      hasCompletedRef.current = true;
      trackProductVideoComplete({ contentPosition, location, playbackId });
    }

    const player = event.target as HTMLMediaElement | null;

    if (player) {
      player.currentTime = 0;
      void player.play();
    }
  };

  return (
    <div
      aria-label={isActive ? (paused ? 'Play video' : 'Pause video') : undefined}
      css={styles.videoSlide}
      onClick={isActive ? togglePlay : undefined}
      onKeyDown={
        isActive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                togglePlay();
              }
            }
          : undefined
      }
      role={isActive ? 'button' : undefined}
      tabIndex={isActive ? 0 : undefined}
    >
      {/* Poster is the base layer; the player fades in over it once it plays. */}
      <img alt={title} css={styles.poster} src={posterUrl} />
      {isActive && (
        <>
          <MuxPlayer
            accentColor={PLAYER_ACCENT_COLOR}
            autoPlay
            css={styles.player}
            metadata={{ player_name: 'browse-productdetails', video_title: title }}
            muted
            nohotkeys
            onEnded={handleVideoEnded}
            onPlaying={() => setReady(true)}
            paused={paused}
            playbackId={playbackId}
            playsInline
            streamType="on-demand"
            style={{ opacity: ready ? 1 : 0 }}
            thumbnailTime={0}
          />
          {onExpand && (
            <button
              aria-label="View video full screen"
              css={styles.expandButton}
              onClick={(event) => {
                event.stopPropagation();
                trackProductVideoExpand({ contentPosition, location, playbackId });
                onExpand();
              }}
              type="button"
            >
              <svg
                aria-hidden="true"
                fill="currentColor"
                height="24"
                viewBox="0 0 24 24"
                width="24"
              >
                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  );
};
